import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../supabase'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Topbar from '../components/Topbar'
import { useToast } from '../context/ToastContext'
import { getCurrencySymbol } from '../utils/currencies'
import { useTranslation } from '../i18n/LanguageContext'

// Design system — CSS vars
const GREEN  = 'var(--success)'
const RED    = 'var(--error)'
const AMBER  = 'var(--warning)'
const PURPLE = '#7C3AED'

const EVENT_TYPES   = ['Fair', 'Stall', 'Exhibition', 'Popup', 'Market', 'Other']
const EVENTS_PER_PAGE = 10

const STATUS_CONFIG = {
  received:     { bg: 'var(--primary-light)',          text: 'var(--primary)', label: 'Received' },
  pending:      { bg: '#FFF8E6',          text: 'var(--warning)', label: 'Pending' },
  confirmed:    { bg: '#FEF3C7',          text: '#92400E',        label: 'Confirmed' },
  processing:   { bg: '#F0FDFA',          text: '#0F766E',        label: 'Processing' },
  with_courier: { bg: '#F5F3FF',          text: '#7C3AED',        label: 'With Courier' },
  shipped:      { bg: '#F0FDF4',          text: 'var(--success)', label: 'Shipped' },
  closed:       { bg: '#F0FDF4',          text: 'var(--success)', label: 'Sold / Closed' },
  return:       { bg: '#FEF2F2',          text: 'var(--error)',   label: 'Return' },
}

const IC = {
  eye:    'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z',
  edit:   'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  trash:  'M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6',
  plus:   'M12 5v14M5 12h14',
  x:      'M18 6L6 18M6 6l12 12',
  search: 'M21 21l-4.35-4.35 M11 19a8 8 0 100-16 8 8 0 000 16z',
  cart:   'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-10 2a2 2 0 100 4 2 2 0 000-4z',
  phone:  'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
}

function Icon({ path, size = 14, color = 'var(--text-muted)', strokeWidth = 1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  )
}

const inp = { width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '9px 12px', fontSize: '12.5px', outline: 'none', background: 'var(--bg-card)', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', boxSizing: 'border-box', transition: 'border-color 0.15s' }

function Inp({ type = 'text', value, onChange, placeholder, min }) {
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)}
      placeholder={placeholder} min={min} style={{ ...inp }}
      onFocus={e => e.target.style.borderColor = 'var(--primary)'}
      onBlur={e => e.target.style.borderColor = 'var(--border)'} />
  )
}

function Sel({ value, onChange, children }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inp }}
      onFocus={e => e.target.style.borderColor = 'var(--primary)'}
      onBlur={e => e.target.style.borderColor = 'var(--border)'}>
      {children}
    </select>
  )
}

function Field({ label, children, optional }) {
  return (
    <div>
      <label style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
        {label}{optional && <span style={{ fontWeight: 400, color: '#B0B3B8', marginLeft: '4px' }}>optional</span>}
      </label>
      {children}
    </div>
  )
}

function StatusPill({ status }) {
  const sc = STATUS_CONFIG[status] || { bg: 'var(--bg-muted)', text: 'var(--text-muted)', label: status }
  return (
    <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: 'var(--radius-full)', background: sc.bg, color: sc.text, whiteSpace: 'nowrap' }}>
      {sc.label}
    </span>
  )
}

function getEventStatus(event, t) {
  const today = new Date()
  const start = new Date(event.start_date)
  const end   = new Date(event.end_date || event.start_date)
  end.setHours(23, 59, 59)
  if (today < start) return { label: t('events.status_upcoming'), bg: 'var(--primary-light)', text: 'var(--primary)', key: 'upcoming' }
  if (today >= start && today <= end) return { label: t('events.status_ongoing'), bg: '#F0FDF4', text: 'var(--success)', key: 'ongoing' }
  return { label: t('events.status_completed'), bg: 'var(--bg-muted)', text: 'var(--text-muted)', key: 'completed' }
}

function MiniBar({ data, color = 'var(--primary)', height = 60, sym = '', highlight = -1 }) {
  if (!data || data.length === 0) return null
  const max = Math.max(...data.map(d => d.value), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: (height + 24) + 'px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
          <div title={sym + Math.round(d.value).toLocaleString()}
            style={{ width: '100%', borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0', background: i === highlight ? 'var(--primary)' : color, opacity: i === highlight ? 1 : 0.75, height: Math.max((d.value / max) * height, d.value > 0 ? 4 : 0) + 'px', transition: 'height 0.4s ease', minHeight: d.value > 0 ? '4px' : '0' }} />
          <div style={{ fontSize: '9px', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: '100%', textAlign: 'center', textOverflow: 'ellipsis' }}>{d.label}</div>
        </div>
      ))}
    </div>
  )
}

function calcLine(item) {
  const base    = Number(item.quantity || 1) * Number(item.unit_price || 0)
  const discAmt = item.line_discount_percent ? Math.round(base * Number(item.line_discount_percent) / 100) : 0
  const vatAmt  = item.line_vat_percent ? Math.round((base - discAmt) * Number(item.line_vat_percent) / 100) : 0
  return { base, discAmt, vatAmt, subtotal: base - discAmt + vatAmt }
}

function emptyCartLine() {
  return { product_id: '', product_code: '', product_name: '', product_color: '', product_image: '', variant_id: '', variant_name: '', quantity: 1, unit_price: '', line_discount_percent: '', line_vat_percent: '' }
}

function EventCartRow({ item, index, onUpdate, onRemove, sym, eventStock, products, t }) {
  const [variantList, setVariantList] = useState([])
  const [loadingVars, setLoadingVars] = useState(false)
  const [showSearch, setShowSearch]   = useState(!item.product_id)
  const [search, setSearch]           = useState('')
  const [showDrop, setShowDrop]       = useState(false)
  const searchRef = useRef(null)
  const calc = calcLine(item)

  const availableProductIds = [...new Set(eventStock.map(s => s.product_id))]
  const availableProducts   = products.filter(p => availableProductIds.includes(p.id))
  const filtered = availableProducts.filter(p =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.product_code?.toLowerCase().includes(search.toLowerCase()) ||
    p.color?.toLowerCase().includes(search.toLowerCase())
  )

  useEffect(() => {
    if (!item.product_id) { setVariantList([]); return }
    setLoadingVars(true)
    supabase.from('product_variants').select('id, variant_name, variant_code, stock, sell_price')
      .eq('product_id', item.product_id).order('variant_name')
      .then(({ data }) => {
        const vList = data || []
        setVariantList(vList); setLoadingVars(false)
        if (vList.length === 1 && !item.variant_id) onUpdate(index, { variant_id: vList[0].id, variant_name: vList[0].variant_name, unit_price: vList[0].sell_price || item.unit_price })
      })
  }, [item.product_id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowDrop(false) }
    document.addEventListener('mousedown', handler); return () => document.removeEventListener('mousedown', handler)
  }, [])

  const stockEntry = eventStock.find(s => s.product_id === item.product_id && (item.variant_id ? s.variant_id === item.variant_id : !s.variant_id))
  const available  = stockEntry ? stockEntry.quantity_sent - stockEntry.quantity_sold - stockEntry.quantity_returned : null

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '12px', marginBottom: '8px', background: 'var(--bg-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('events.qs_product_label')} {index + 1}</div>
        {index > 0 && (
          <button onClick={() => onRemove(index)}
            style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#FEF2F2', border: '1px solid #FECACA', color: RED, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        )}
      </div>

      {showSearch || !item.product_id ? (
        <div style={{ marginBottom: '10px', position: 'relative' }} ref={searchRef}>
          <div style={{ position: 'relative' }}>
            <input value={search} onChange={e => { setSearch(e.target.value); setShowDrop(true) }} onFocus={() => setShowDrop(true)}
              placeholder={t('events.qs_search_ph')} style={{ ...inp, paddingLeft: '34px' }} autoFocus />
            <div style={{ position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)' }}><Icon path={IC.search} color={'var(--text-muted)'} size={13} /></div>
          </div>
          {showDrop && (
            <div style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--bg-card)', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '220px', overflowY: 'auto' }}>
              {filtered.length === 0 ? (
                <div style={{ padding: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>{t('events.stock_no_products')}</div>
              ) : filtered.map(p => {
                const es    = eventStock.filter(s => s.product_id === p.id)
                const avail = es.reduce((sum, s) => sum + (s.quantity_sent - s.quantity_sold - s.quantity_returned), 0)
                return (
                  <div key={p.id}
                    onMouseDown={() => { onUpdate(index, { product_id: p.id, product_code: p.product_code, product_name: p.name, product_color: p.color || '', product_image: p.image_url || '', unit_price: p.sell_price || 0, variant_id: '', variant_name: '' }); setShowSearch(false); setShowDrop(false); setSearch('') }}
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}>
                    <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm)', flexShrink: 0, overflow: 'hidden', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {p.image_url ? <img src={p.image_url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon path="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" color={'var(--text-muted)'} size={16} strokeWidth={1.5} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                        <span style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)' }}>{p.name}</span>
                        {p.color && <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: '#F5F3FF', color: PURPLE }}>{p.color}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '10px', fontSize: '11px' }}>
                        <span style={{ color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{p.product_code}</span>
                        <span style={{ color: avail <= 0 ? RED : avail <= 3 ? AMBER : GREEN }}>{avail} {t('events.qs_at_event')}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', flexShrink: 0 }}>{sym}{Number(p.sell_price || 0).toLocaleString()}</div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: '10px 13px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: '1px solid var(--primary-border)', marginBottom: '10px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
            <div style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm)', flexShrink: 0, overflow: 'hidden', background: 'var(--bg-muted)' }}>
              {item.product_image ? <img src={item.product_image} alt={item.product_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon path="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" color={'var(--text-muted)'} size={16} strokeWidth={1.5} /></div>}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{item.product_name}</span>
                {item.product_color && <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: '#F5F3FF', color: PURPLE }}>{item.product_color}</span>}
              </div>
              <span style={{ fontSize: '11.5px', color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{item.product_code}</span>
              {available !== null && <span style={{ fontSize: '11px', color: available <= 0 ? RED : available <= 3 ? AMBER : GREEN, marginLeft: '8px' }}>{available} {t('events.qs_at_event')}</span>}
            </div>
          </div>
          <button onClick={() => { onUpdate(index, { product_id: '', product_code: '', product_name: '', product_color: '', product_image: '', variant_id: '', variant_name: '' }); setShowSearch(true); setVariantList([]) }}
            style={{ fontSize: '11.5px', fontWeight: 600, padding: '5px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0 }}>
            {t('events.qs_change')}
          </button>
        </div>
      )}

      {item.product_id && (
        <div style={{ marginBottom: '10px' }}>
          {loadingVars ? (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t('events.stock_loading_var')}</div>
          ) : variantList.length === 0 ? (
            <div style={{ fontSize: '11.5px', color: GREEN }}>{t('events.stock_no_variant')}</div>
          ) : (
            <>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '7px' }}>{t('events.stock_select_variant')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {variantList.map(v => {
                  const isSelected = item.variant_id === v.id
                  const isOut = v.stock === 0
                  return (
                    <button key={v.id}
                      onClick={() => !isOut && onUpdate(index, { variant_id: v.id, variant_name: v.variant_name, unit_price: v.sell_price || item.unit_price })}
                      disabled={isOut}
                      style={{ padding: '7px 12px', borderRadius: 'var(--radius-md)', border: '2px solid', borderColor: isSelected ? 'var(--primary)' : 'var(--border)', background: isSelected ? 'var(--primary-light)' : isOut ? 'var(--bg-muted)' : 'var(--bg-card)', opacity: isOut ? 0.5 : 1, cursor: isOut ? 'not-allowed' : 'pointer', textAlign: 'left' }}>
                      <div style={{ fontSize: '12.5px', fontWeight: 700, color: isSelected ? 'var(--primary)' : 'var(--text-primary)' }}>{v.variant_name}{isSelected && ' ✓'}</div>
                      <div style={{ fontSize: '10.5px', color: isOut ? RED : v.stock <= 5 ? AMBER : GREEN }}>{isOut ? 'Out of stock' : v.stock + ' left'}</div>
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
        <Field label={t('events.qs_quantity')}><Inp type="number" value={item.quantity} onChange={v => onUpdate(index, { quantity: v })} min="1" placeholder="1" /></Field>
        <Field label={t('events.qs_price') + ' (' + sym + ')'}><Inp type="number" value={item.unit_price} onChange={v => onUpdate(index, { unit_price: v })} placeholder="0" /></Field>
      </div>

      <div style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{item.quantity} × {sym}{Number(item.unit_price || 0).toLocaleString()}</div>
        <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{sym}{calc.subtotal.toLocaleString()}</div>
      </div>
    </div>
  )
}

function Events() {
  const { seller, isFeatureAllowed } = useAuth()
  const navigate   = useNavigate()
  const { showToast } = useToast()
  const { t }      = useTranslation()
  const isInt      = seller?.seller_mode === 'international'
  const currency   = seller?.seller_currency || 'BDT'
  const sym        = getCurrencySymbol(currency)
  const fmt        = useCallback((n) => {
    const abs = Math.abs(Math.round(n || 0))
    if (abs >= 100000) return sym + Math.round(abs / 1000) + 'k'
    return sym + abs.toLocaleString()
  }, [sym])

  const [page, setPage]               = useState('list')
  const [events, setEvents]           = useState([])
  const [loading, setLoading]         = useState(true)
  const [showAdd, setShowAdd]         = useState(false)
  const [saving, setSaving]           = useState(false)
  const [selected, setSelected]       = useState(null)
  const [activeTab, setActiveTab]     = useState('stock')
  const [eventOrders, setEventOrders] = useState([])
  const [eventStock, setEventStock]   = useState([])
  const [products, setProducts]       = useState([])
  const [productVariants, setProductVariants] = useState([])
  const [showAddStock, setShowAddStock] = useState(false)
  const [savingStock, setSavingStock]   = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [sellMode, setSellMode]       = useState('instant')
  const [cart, setCart]               = useState([emptyCartLine()])
  const [selling, setSelling]         = useState(false)
  const [orderDiscount, setOrderDiscount] = useState('')
  const [orderVat, setOrderVat]       = useState('')
  const [paymentMethod, setPaymentMethod] = useState('Cash')
  const [customerName, setCustomerName]   = useState('')
  const [customerPhone, setCustomerPhone] = useState('')
  const [takenBy, setTakenBy]         = useState('')
  const [saleNote, setSaleNote]       = useState('')
  const [pnl, setPnl]                 = useState({ revenue: 0, costs: 0 })
  const [currentPage, setCurrentPage] = useState(1)
  const [newStock, setNewStock]       = useState({ product_id: '', variant_id: '', quantity_sent: '', notes: '' })
  const [receiptData, setReceiptData] = useState(null)
  const [searchQuery, setSearchQuery]   = useState('')
  const [showEditForm, setShowEditForm]  = useState(false) // eslint-disable-line
  const [editForm, setEditForm]          = useState(null) // eslint-disable-line

  const [form, setForm] = useState({
    name: '', event_type: 'Fair', location: '',
    start_date: '', end_date: '', stall_fee: '',
    transport_cost: '', other_costs: '', notes: '',
    organizer_name: '', organizer_phone: '',
  })

  const QS_PAYMENTS = isInt
    ? ['Cash', 'Card / POS', 'PayID', 'PayNow', 'Zelle', 'Venmo', 'Wise', 'Bank transfer', 'Other']
    : ['Cash', 'Card / POS', 'bKash', 'Nagad', 'Rocket', 'Bank transfer', 'Other']

  const lineCalcs     = cart.map(calcLine)
  const itemsSubtotal = lineCalcs.reduce((s, l) => s + l.subtotal, 0)
  const orderDiscAmt  = orderDiscount ? Math.round(itemsSubtotal * Number(orderDiscount) / 100) : 0
  const orderVatAmt   = orderVat ? Math.round((itemsSubtotal - orderDiscAmt) * Number(orderVat) / 100) : 0
  const grandTotal    = itemsSubtotal - orderDiscAmt + orderVatAmt

  useEffect(() => {
    if (seller?.id) { fetchEvents(); fetchProducts() }
  }, [seller]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return
    const revenue = eventOrders.filter(o => o.status !== 'return').reduce((s, o) => s + Number(o.total_amount || 0), 0)
    const costs   = Number(selected.stall_fee || 0) + Number(selected.transport_cost || 0) + Number(selected.other_costs || 0)
    setPnl({ revenue, costs })
  }, [eventOrders, selected])

  const fetchEvents = async () => {
    setLoading(true)
    const { data } = await supabase.from('seller_events').select('*').eq('seller_id', seller.id).order('start_date', { ascending: false })
    setEvents(data || []); setLoading(false)
  }

  const fetchProducts = async () => {
    const { data } = await supabase.from('products').select('id, name, product_code, stock, sell_price, color, image_url').eq('seller_id', seller.id).eq('status', 'active').order('name')
    setProducts(data || [])
  }

  const fetchEventDetails = async (event) => {
    const ev = event || selected; if (!ev) return
    const { data: orders } = await supabase.from('orders').select('*, customers(name, phone)').eq('event_id', ev.id).order('created_at', { ascending: false })
    setEventOrders(orders || [])
    const { data: stock } = await supabase.from('event_stock').select('*, products(name, product_code, color, image_url)').eq('event_id', ev.id).order('created_at', { ascending: true })
    setEventStock(stock || [])
  }


  // eslint-disable-next-line
  const exportPnL = () => {
    if (!selected) return
    const rows = [
      ['Event', selected.name],
      ['Date', selected.start_date + (selected.end_date ? ' → ' + selected.end_date : '')],
      ['Location', selected.location || ''],
      [''],
      ['Revenue', pnl.revenue],
      ['Stall fee', selected.stall_fee || 0],
      ['Transport', selected.transport_cost || 0],
      ['Other costs', selected.other_costs || 0],
      ['Total costs', pnl.costs],
      ['Profit / Loss', pnl.revenue - pnl.costs],
      ['ROI %', pnl.costs > 0 ? Math.round(((pnl.revenue - pnl.costs) / pnl.costs) * 100) + '%' : 'N/A'],
      [''],
      ['Orders', ...Object.keys(eventOrders[0] || {})],
      ...eventOrders.map(o => [o.order_code, o.product_name, o.quantity, o.unit_price, o.total_amount, o.payment_method, o.customer_name||'', o.status]),
    ]
    const csv = rows.map(r => r.map(v => '"' + String(v||'').replace(/"/g,'""') + '"').join(',')).join('\n')
    const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    a.download = (selected.name || 'event') + '-report.csv'; a.click()
  }

  const openEvent = async (event) => {
    setSelected(event); setPage('detail'); setActiveTab('stock'); setCart([emptyCartLine()])
    await fetchEventDetails(event)
  }

  const fetchVariantsForProduct = async (productId) => {
    if (!productId) { setProductVariants([]); return }
    const { data } = await supabase.from('product_variants').select('*').eq('product_id', productId).order('variant_name')
    setProductVariants(data || [])
    setNewStock(s => ({ ...s, variant_id: '' }))
  }

  const handleSaveEvent = async () => {
    if (!form.name || !form.start_date) return
    setSaving(true)
    await supabase.from('seller_events').insert({
      seller_id: seller.id, name: form.name, event_type: form.event_type,
      location: form.location || null, start_date: form.start_date,
      end_date: form.end_date || form.start_date,
      stall_fee: Number(form.stall_fee) || 0,
      transport_cost: Number(form.transport_cost) || 0,
      other_costs: Number(form.other_costs) || 0,
      notes: form.notes || null, status: 'upcoming',
      organizer_name: form.organizer_name || null,
      organizer_phone: form.organizer_phone || null,
    })
    await fetchEvents()
    setForm({ name: '', event_type: 'Fair', location: '', start_date: '', end_date: '', stall_fee: '', transport_cost: '', other_costs: '', notes: '', organizer_name: '', organizer_phone: '' })
    setShowAdd(false); setSaving(false)
    showToast(t('events.toast_created'))
  }

  const handleAddStock = async () => {
    if (!newStock.product_id || !newStock.quantity_sent) return
    if (productVariants.length > 0 && !newStock.variant_id) return
    setSavingStock(true)
    const qty     = Number(newStock.quantity_sent)
    const product = products.find(p => p.id === newStock.product_id)
    const variant = productVariants.find(v => v.id === newStock.variant_id)
    await supabase.from('event_stock').insert({
      event_id: selected.id, product_id: newStock.product_id, seller_id: seller.id,
      variant_id: newStock.variant_id || null, variant_name: variant?.variant_name || null,
      quantity_sent: qty, quantity_returned: 0, quantity_sold: 0, notes: newStock.notes || null,
    })
    if (variant) await supabase.from('product_variants').update({ stock: Math.max(0, (variant.stock || 0) - qty) }).eq('id', variant.id)
    else await supabase.from('products').update({ stock: Math.max(0, (product?.stock || 0) - qty) }).eq('id', newStock.product_id)
    await supabase.from('stock_log').insert({ product_id: newStock.product_id, action: 'deducted', quantity: qty, note: 'Moved to event: ' + selected.name, done_by: 'Owner' })
    await fetchProducts(); await fetchEventDetails(selected)
    setNewStock({ product_id: '', variant_id: '', quantity_sent: '', notes: '' }); setProductVariants([])
    setShowAddStock(false); setSavingStock(false)
    showToast(t('events.stock_toast_moved'))
  }

  const handleReturnStock = async (stockItem) => {
    const remaining = stockItem.quantity_sent - stockItem.quantity_sold - stockItem.quantity_returned
    if (remaining <= 0) return
    await supabase.from('event_stock').update({ quantity_returned: stockItem.quantity_returned + remaining }).eq('id', stockItem.id)
    if (stockItem.variant_id) {
      const { data: v } = await supabase.from('product_variants').select('stock').eq('id', stockItem.variant_id).single()
      await supabase.from('product_variants').update({ stock: (v?.stock || 0) + remaining }).eq('id', stockItem.variant_id)
    } else {
      const product = products.find(p => p.id === stockItem.product_id)
      await supabase.from('products').update({ stock: (product?.stock || 0) + remaining }).eq('id', stockItem.product_id)
    }
    await supabase.from('stock_log').insert({ product_id: stockItem.product_id, action: 'restock', quantity: remaining, note: 'Returned from event: ' + selected.name, done_by: 'Owner' })
    await fetchProducts(); await fetchEventDetails(selected)
    showToast(remaining + ' ' + (remaining !== 1 ? t('events.stock_units_pl') : t('events.stock_units')) + ' ' + t('events.stock_toast_returned'))
  }

  const handleDeleteStockRow = async (stockItem) => {
    const remaining = stockItem.quantity_sent - stockItem.quantity_sold - stockItem.quantity_returned
    if (remaining > 0) {
      if (stockItem.variant_id) {
        const { data: v } = await supabase.from('product_variants').select('stock').eq('id', stockItem.variant_id).single()
        await supabase.from('product_variants').update({ stock: (v?.stock || 0) + remaining }).eq('id', stockItem.variant_id)
      } else {
        const product = products.find(p => p.id === stockItem.product_id)
        await supabase.from('products').update({ stock: (product?.stock || 0) + remaining }).eq('id', stockItem.product_id)
      }
    }
    await supabase.from('event_stock').delete().eq('id', stockItem.id)
    await fetchProducts(); await fetchEventDetails(selected)
    showToast(t('events.stock_toast_removed'))
  }

  const handleDeleteEvent = async () => {
    await supabase.from('event_stock').delete().eq('event_id', selected.id)
    await supabase.from('orders').update({ event_id: null, event_name: null }).eq('event_id', selected.id)
    await supabase.from('seller_events').delete().eq('id', selected.id)
    setSelected(null); setPage('list'); setShowDeleteConfirm(false)
    await fetchEvents()
    showToast(t('events.toast_deleted'))
  }

  const handleQuickSell = async () => {
    const validLines = cart.filter(l => l.product_id)
    if (validLines.length === 0) { showToast(t('events.qs_error_no_product'), 'error'); return }
    const productIds = validLines.map(l => l.product_id).filter(Boolean)
    const { data: variantCounts } = await supabase.from('product_variants').select('product_id').in('product_id', productIds)
    const productsWithVariants = new Set((variantCounts || []).map(v => v.product_id))
    const missingVariant = validLines.find(l => productsWithVariants.has(l.product_id) && !l.variant_id)
    if (missingVariant) { showToast(t('events.qs_error_variant') + ' ' + missingVariant.product_name, 'error'); return }

    setSelling(true)
    const firstLine   = validLines[0]
    const isMulti     = validLines.length > 1
    const orderCode   = 'ORD-' + new Date().getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000)
    const nowISO      = new Date().toISOString()
    const orderStatus = sellMode === 'instant' ? 'closed' : sellMode === 'courier' ? 'received' : 'pending'
    const payStatus   = sellMode === 'instant' ? 'Paid in full' : 'Unpaid COD'
    const amountPaid  = sellMode === 'instant' ? grandTotal : 0

    let customerId = null
    if (customerName || customerPhone) {
      const { data: nc } = await supabase.from('customers').insert({ seller_id: seller.id, name: customerName || 'Walk-in', phone: customerPhone || null, country_code: isInt ? '+61' : '+880', country: isInt ? '' : 'Bangladesh' }).select().single()
      customerId = nc?.id
    }

    const { data: newOrder, error } = await supabase.from('orders').insert({
      seller_id: seller.id, customer_id: customerId, order_code: orderCode,
      product_id: firstLine.product_id, product_code: firstLine.product_code,
      product_name: firstLine.product_name, variant_id: firstLine.variant_id || null,
      variant_name: firstLine.variant_name || null, quantity: Number(firstLine.quantity || 1),
      unit_price: Number(firstLine.unit_price || 0), delivery_charge: 0,
      total_amount: grandTotal, items_subtotal: itemsSubtotal, has_multiple_items: isMulti,
      discount_percent: orderDiscount || null, discount_amount: orderDiscAmt || null,
      vat_percent: orderVat || null, vat_amount: orderVatAmt || null,
      payment_method: paymentMethod, payment_status: payStatus, amount_paid: amountPaid,
      order_source: 'Event / Fair', submitted_by: takenBy || 'Seller', taken_by: takenBy || null,
      status: orderStatus, event_id: selected.id, event_name: selected.name,
      notes: saleNote || null, created_at: nowISO,
    }).select('*, customers(name, phone)').single()

    if (error) { showToast('Error: ' + error.message, 'error'); setSelling(false); return }

    const itemRows = validLines.map(l => {
      const lc = calcLine(l)
      return { order_id: newOrder.id, seller_id: seller.id, product_id: l.product_id, product_code: l.product_code, product_name: l.product_name, variant_id: l.variant_id || null, variant_name: l.variant_name || null, quantity: Number(l.quantity || 1), unit_price: Number(l.unit_price || 0), line_subtotal: lc.subtotal }
    })
    await supabase.from('order_items').insert(itemRows)

    if (sellMode === 'instant') {
      for (const l of validLines) {
        const stockItem = eventStock.find(s => s.product_id === l.product_id && (l.variant_id ? s.variant_id === l.variant_id : !s.variant_id))
        if (stockItem) await supabase.from('event_stock').update({ quantity_sold: (stockItem.quantity_sold || 0) + Number(l.quantity || 1) }).eq('id', stockItem.id)
      }
    }

    await fetchEventDetails(selected)
    setSelling(false)

    if (sellMode === 'instant') {
      setReceiptData({ order: newOrder, items: validLines, total: grandTotal })
    } else {
      showToast(orderCode + ' ' + (sellMode === 'courier' ? t('events.qs_saved_deliver') : t('events.qs_saved_lead')))
    }

    setCart([emptyCartLine()]); setOrderDiscount(''); setOrderVat('')
    setCustomerName(''); setCustomerPhone(''); setTakenBy(''); setSaleNote(''); setPaymentMethod('Cash')
  }

  const totalPages  = Math.ceil(events.length / EVENTS_PER_PAGE)
  const pagedEvents = events.slice((currentPage - 1) * EVENTS_PER_PAGE, currentPage * EVENTS_PER_PAGE)
  const ongoingEvents   = pagedEvents.filter(e => getEventStatus(e, t).key === 'ongoing')
  const upcomingEvents  = pagedEvents.filter(e => getEventStatus(e, t).key === 'upcoming')
  const completedEvents = pagedEvents.filter(e => getEventStatus(e, t).key === 'completed')
  const totalCosts = events.reduce((s, e) => s + Number(e.stall_fee || 0) + Number(e.transport_cost || 0) + Number(e.other_costs || 0), 0)

  // Chart data — costs per event (last 10)
  const costsBarData = [...events].sort((a, b) => new Date(a.start_date) - new Date(b.start_date)).slice(-10).map(e => ({
    label: e.name.slice(0, 8),
    value: Number(e.stall_fee || 0) + Number(e.transport_cost || 0) + Number(e.other_costs || 0),
  }))

  // Events by type for pie-style bar
  const typeMap = {}
  events.forEach(e => { typeMap[e.event_type] = (typeMap[e.event_type] || 0) + 1 })
  const typeBarData = Object.entries(typeMap).sort((a,b) => b[1] - a[1]).map(([label, value]) => ({ label, value }))

  const profit = pnl.revenue - pnl.costs

  // ── EventRow defined inside Events so CSS vars apply ──
  // eslint-disable-next-line
  const handleEditEvent = async () => {
    if (!editForm || !selected) return
    setSaving(true)
    await supabase.from('events').update({
      name: editForm.name, event_type: editForm.event_type, location: editForm.location,
      start_date: editForm.start_date, end_date: editForm.end_date || null,
      organizer_name: editForm.organizer_name, organizer_phone: editForm.organizer_phone,
      stall_fee: editForm.stall_fee || null, transport_cost: editForm.transport_cost || null,
      other_costs: editForm.other_costs || null, notes: editForm.notes,
    }).eq('id', selected.id)
    const updated = { ...selected, ...editForm }
    setSelected(updated)
    setShowEditForm(false)
    setSaving(false)
    showToast('Event updated')
  }

  function EventRow({ event, idx, onClick }) {
    const status = getEventStatus(event, t)
    const costs  = Number(event.stall_fee || 0) + Number(event.transport_cost || 0) + Number(event.other_costs || 0)
    return (
      <div style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.12s' }}
        onClick={onClick}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}>
        {/* Desktop */}
        <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 100px 120px 120px 120px 90px', gap: '8px', padding: '12px 16px', alignItems: 'center' }}
          className="ev-row-desktop">
          <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)' }}>{idx + 1}</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>{event.name}</div>
            {event.location && <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{event.location}</div>}
            {event.organizer_name && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Icon path={IC.phone} size={10} color={'var(--text-muted)'} strokeWidth={2} />
                {event.organizer_name}{event.organizer_phone ? ' · ' + event.organizer_phone : ''}
              </div>
            )}
          </div>
          <div>
            <span style={{ fontSize: '11.5px', fontWeight: 600, padding: '2px 8px', borderRadius: 'var(--radius-full)', background: 'var(--bg-muted)', color: 'var(--text-muted)' }}>{event.event_type}</span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{new Date(event.start_date).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' })}</div>
          <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{event.end_date ? new Date(event.end_date).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}</div>
          <div style={{ fontSize: '12.5px', fontWeight: 700, color: costs > 0 ? RED : 'var(--text-muted)' }}>{costs > 0 ? fmt(costs) : '—'}</div>
          <div><span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: 'var(--radius-full)', background: status.bg, color: status.text }}>{status.label}</span></div>
        </div>
        {/* Mobile */}
        <div style={{ padding: '14px 16px' }} className="ev-row-mobile">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{event.name}</span>
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: 'var(--radius-full)', background: status.bg, color: status.text }}>{status.label}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11.5px', color: 'var(--text-muted)' }}>
            <span>{event.event_type}{event.location ? ' · ' + event.location : ''}</span>
            {costs > 0 && <span style={{ color: RED }}>{fmt(costs)}</span>}
          </div>
          <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>{new Date(event.start_date).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
          {event.organizer_name && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{event.organizer_name}{event.organizer_phone ? ' · ' + event.organizer_phone : ''}</div>}
        </div>
      </div>
    )
  }

  if (!isFeatureAllowed('events')) {
    return (
      <div style={{ flex: 1, background: 'var(--bg-muted)', fontFamily: 'var(--font-ui)' }}>
        <Topbar title={t('events.title')} />
        <div style={{ padding: '48px 24px', maxWidth: '480px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: '40px', border: '1px solid var(--border)' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', display: 'block' }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>{t('events.upgrade_title')}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '24px', lineHeight: 1.6 }}>{t('events.upgrade_desc')}</div>
            <button onClick={() => navigate('/pricing')}
              style={{ width: '100%', padding: '13px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
              {t('events.upgrade_btn')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── EVENT LIST ─────────────────────────────────────────────────────────────
  if (page === 'list') {
    return (
      <div style={{ flex: 1, overflowX: 'hidden', background: 'var(--bg-muted)', fontFamily: 'var(--font-ui)' }}>
        <Topbar title={t('events.title')} />
        <div style={{ padding: '18px 24px' }}>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>{t('events.title')}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{t('events.subtitle')}</div>
            </div>
            <button onClick={() => setShowAdd(!showAdd)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: 'var(--radius-md)', background: showAdd ? 'var(--bg-card)' : 'var(--primary)', border: showAdd ? '1px solid var(--border)' : 'none', fontSize: '13px', fontWeight: 600, color: showAdd ? 'var(--text-muted)' : 'var(--bg-card)', cursor: 'pointer' }}>
              <Icon path={IC.plus} color={showAdd ? 'var(--text-muted)' : 'var(--bg-card)'} size={13} strokeWidth={2.5} />
              {showAdd ? t('events.cancel_btn') : t('events.create_btn')}
            </button>
          </div>

          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }} className="ev-stat-grid">
            {[
              { label: t('events.stat_total'),    value: events.length,                                                          sub: events.length + ' events tracked',                                                              color: 'var(--text-primary)', accent: 'var(--primary)' },
              { label: t('events.stat_ongoing'),   value: events.filter(e => getEventStatus(e,t).key==='ongoing').length,         sub: t('events.stat_ongoing_sub'),                                                                   color: 'var(--success)',      accent: 'var(--success)' },
              { label: t('events.stat_upcoming'),  value: events.filter(e => getEventStatus(e,t).key==='upcoming').length,        sub: t('events.stat_upcoming_sub'),                                                                  color: 'var(--primary)',      accent: 'var(--primary)' },
              { label: t('events.stat_costs'),     value: fmt(totalCosts),                                                        sub: t('events.stat_costs_sub'),                                                                     color: 'var(--warning)',      accent: 'var(--warning)' },
            ].map((s, i) => (
              <div key={i} className="pc-card-hover" style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: s.accent }} />
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>{s.label}</div>
                <div style={{ fontSize: '24px', fontWeight: 800, color: s.color, letterSpacing: '-0.5px', lineHeight: 1, marginBottom: '6px' }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.sub}</div>
              </div>
            ))}
          </div>

          {/* Charts row */}
          {events.length > 0 && (
            <div className="pc-fade-up ev-chart-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '14px' }}>
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' }}>{t('events.costs_chart_title')}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '12px' }}>{t('events.costs_chart_sub')}</div>
                <MiniBar data={costsBarData} color={RED} height={70} sym={sym} />
              </div>
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', boxShadow: 'var(--shadow-sm)' }}>
                <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px' }}>Events by type</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {typeBarData.map((d, i) => (
                    <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)', minWidth: '70px' }}>{d.label}</div>
                      <div style={{ flex: 1, height: '6px', borderRadius: 'var(--radius-sm)', background: 'var(--border)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: Math.round((d.value / typeBarData[0].value) * 100) + '%', background: 'var(--primary)', borderRadius: 'var(--radius-sm)' }} />
                      </div>
                      <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--primary)', minWidth: '16px', textAlign: 'right' }}>{d.value}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Create form */}
          {showAdd && (
            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '14px' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '3px', height: '14px', borderRadius: '2px', background: 'rgba(255,255,255,0.5)' }} />
                <div style={{ fontSize: '12px', fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{t('events.form_title')}</div>
              </div>
              <div style={{ padding: '16px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }} className="ev-grid">
                  <div style={{ gridColumn: 'span 2' }}>
                    <Field label={t('events.form_name') + ' *'}><Inp value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} placeholder="e.g. Dhaka Craft Fair 2026" /></Field>
                  </div>
                  <Field label={t('events.form_type')}>
                    <Sel value={form.event_type} onChange={v => setForm(f => ({ ...f, event_type: v }))}>
                      {EVENT_TYPES.map(et => <option key={et}>{et}</option>)}
                    </Sel>
                  </Field>
                  <Field label={t('events.form_location')} optional><Inp value={form.location} onChange={v => setForm(f => ({ ...f, location: v }))} placeholder="e.g. Bashundhara City" /></Field>
                  <Field label={t('events.form_start') + ' *'}><Inp type="date" value={form.start_date} onChange={v => setForm(f => ({ ...f, start_date: v }))} /></Field>
                  <Field label={t('events.form_end')} optional><Inp type="date" value={form.end_date} onChange={v => setForm(f => ({ ...f, end_date: v }))} /></Field>
                  <Field label={t('events.form_organizer')} optional><Inp value={form.organizer_name} onChange={v => setForm(f => ({ ...f, organizer_name: v }))} placeholder={t('events.form_organizer_ph')} /></Field>
                  <Field label={t('events.form_organizer_phone')} optional><Inp value={form.organizer_phone} onChange={v => setForm(f => ({ ...f, organizer_phone: v }))} placeholder={t('events.form_organizer_phone_ph')} /></Field>
                  <Field label={t('events.form_stall') + ' (' + sym + ')'} optional><Inp type="number" value={form.stall_fee} onChange={v => setForm(f => ({ ...f, stall_fee: v }))} placeholder="0" /></Field>
                  <Field label={t('events.form_transport') + ' (' + sym + ')'} optional><Inp type="number" value={form.transport_cost} onChange={v => setForm(f => ({ ...f, transport_cost: v }))} placeholder="0" /></Field>
                  <Field label={t('events.form_other') + ' (' + sym + ')'} optional><Inp type="number" value={form.other_costs} onChange={v => setForm(f => ({ ...f, other_costs: v }))} placeholder="0" /></Field>
                  <Field label={t('events.form_notes')} optional><Inp value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} placeholder="Optional" /></Field>
                </div>
                <button onClick={handleSaveEvent} disabled={saving || !form.name || !form.start_date}
                  style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer', opacity: (saving || !form.name || !form.start_date) ? 0.6 : 1 }}>
                  {saving ? t('events.form_saving') : t('events.form_save')}
                </button>
              </div>
            </div>
          )}

          {/* Search bar */}
          <div style={{ position: 'relative', marginBottom: '10px' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search events by name, type or location..."
              style={{ ...inp, paddingLeft: '32px' }}
              onFocus={e => e.target.style.borderColor='var(--primary)'}
              onBlur={e => e.target.style.borderColor='var(--border)'} />
          </div>

          {/* Event list */}

          {loading ? (
            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', border: '2.5px solid var(--primary)', borderTopColor: 'transparent', animation: 'ev-spin 0.7s linear infinite', margin: '0 auto 12px' }} />
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{t('events.loading')}</div>
            </div>
          ) : events.length === 0 ? (
            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto 12px", display: "block" }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{t('events.no_events')}</div>
              <div style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{t('events.no_events_desc')}</div>
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '12px' }}>
                {/* Table header — desktop only */}
                <div style={{ display: 'grid', gridTemplateColumns: '36px 1fr 100px 120px 120px 120px 90px', gap: '8px', padding: '10px 16px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)' }} className="ev-row-desktop">
                  {[t('events.col_sl'), t('events.col_event'), t('events.col_type'), t('events.col_start'), t('events.col_end'), t('events.col_costs'), t('events.col_status')].map(h => (
                    <div key={h} style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
                  ))}
                </div>

                {ongoingEvents.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', background: '#F0FDF4', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: GREEN, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('events.section_ongoing')}</span>
                    </div>
                    {ongoingEvents.map((event, idx) => <EventRow key={event.id} event={event} idx={(currentPage - 1) * EVENTS_PER_PAGE + idx} onClick={() => openEvent(event)} />)}
                  </>
                )}
                {upcomingEvents.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', background: 'var(--primary-light)', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('events.section_upcoming')}</span>
                    </div>
                    {upcomingEvents.map((event, idx) => <EventRow key={event.id} event={event} idx={(currentPage - 1) * EVENTS_PER_PAGE + ongoingEvents.length + idx} onClick={() => openEvent(event)} />)}
                  </>
                )}
                {completedEvents.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('events.section_completed')}</span>
                    </div>
                    {completedEvents.map((event, idx) => <EventRow key={event.id} event={event} idx={(currentPage - 1) * EVENTS_PER_PAGE + ongoingEvents.length + upcomingEvents.length + idx} onClick={() => openEvent(event)} />)}
                  </>
                )}
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {t('events.showing')} {(currentPage - 1) * EVENTS_PER_PAGE + 1}–{Math.min(currentPage * EVENTS_PER_PAGE, events.length)} {t('events.of')} {events.length} {t('events.events_word')}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                      style={{ padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: currentPage === 1 ? 'var(--text-muted)' : 'var(--text-primary)', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
                      {t('events.prev')}
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                      <button key={p} onClick={() => setCurrentPage(p)}
                        style={{ width: '32px', height: '32px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: p === currentPage ? 'var(--primary)' : 'var(--border)', background: p === currentPage ? 'var(--primary)' : 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: p === currentPage ? 'var(--bg-card)' : 'var(--text-primary)', cursor: 'pointer' }}>
                        {p}
                      </button>
                    ))}
                    <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                      style={{ padding: '6px 12px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: currentPage === totalPages ? 'var(--text-muted)' : 'var(--text-primary)', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
                      {t('events.next')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <style>{`
          @keyframes ev-spin { to { transform: rotate(360deg) } }
          @keyframes pc-fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
          .pc-fade-up { animation: pc-fade-up 0.4s ease-out both; }
          .pc-card-hover { transition: transform 0.15s ease, box-shadow 0.15s ease; }
          .pc-card-hover:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(11,31,58,0.08) !important; }
          .ev-row-desktop { display: grid !important; } .ev-row-mobile { display: none !important; }
          @media(max-width:800px) { .ev-stat-grid { grid-template-columns: 1fr 1fr !important; } .ev-row-desktop { display: none !important; } .ev-row-mobile { display: block !important; } .ev-grid { grid-template-columns: 1fr !important; } .ev-chart-grid { grid-template-columns: 1fr !important; } }
        `}</style>
      </div>
    )
  }

  // ── EVENT DETAIL ───────────────────────────────────────────────────────────
  const evStatus = selected ? getEventStatus(selected, t) : {}

  return (
    <div style={{ flex: 1, overflowX: 'hidden', background: 'var(--bg-muted)', fontFamily: 'var(--font-ui)' }}>
      <Topbar title={selected?.name || t('events.title')} />
      <div style={{ padding: '18px 24px' }}>

        {/* Back + header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => { setPage('list'); setSelected(null) }}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer', flexShrink: 0 }}>
            {t('events.back')}
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
              <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' }}>{selected?.name}</div>
              <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: 'var(--radius-full)', background: evStatus.bg, color: evStatus.text }}>{evStatus.label}</span>
            </div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
              {selected?.event_type}
              {selected?.location ? ' · ' + selected.location : ''}
              {selected?.start_date ? ' · ' + new Date(selected.start_date).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
              {selected?.end_date && selected.end_date !== selected.start_date ? ' → ' + new Date(selected.end_date).toLocaleDateString('en-BD', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
            </div>
            {(selected?.organizer_name || selected?.organizer_phone) && (
              <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Icon path={IC.phone} size={12} color={'var(--text-muted)'} strokeWidth={2} />
                {t('events.organizer')}: <strong style={{ color: 'var(--text-primary)' }}>{selected?.organizer_name}</strong>
                {selected?.organizer_phone && <a href={'tel:' + selected.organizer_phone} style={{ color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>{selected.organizer_phone}</a>}
              </div>
            )}
          </div>
        </div>

        {/* P&L cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }} className="ev-stat-grid">
          {[
            { label: t('events.pnl_revenue'), value: fmt(pnl.revenue), color: GREEN, sub: eventOrders.filter(o => o.status !== 'return').length + ' ' + t('events.pnl_orders') },
            { label: t('events.pnl_costs'),   value: fmt(pnl.costs),   color: RED,   sub: t('events.pnl_stall_sub') },
            { label: profit >= 0 ? t('events.pnl_profit') : t('events.pnl_loss'), value: fmt(Math.abs(profit)), color: profit >= 0 ? GREEN : RED, sub: pnl.costs > 0 ? t('events.pnl_roi') + ' ' + Math.round((profit / pnl.costs) * 100) + '%' : t('events.pnl_no_costs') },
            { label: t('events.pnl_avg'), value: eventOrders.filter(o => o.status !== 'return').length > 0 ? fmt(pnl.revenue / eventOrders.filter(o => o.status !== 'return').length) : sym + '0', color: 'var(--primary)', sub: t('events.pnl_per_tx') },
          ].map((s, i) => (
            <div key={i} className='pc-card-hover' style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', position: 'relative', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: s.color }} />
              <div style={{ fontSize: '11.5px', color: 'var(--text-muted)', marginBottom: '4px' }}>{s.label}</div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: s.color, marginBottom: '2px' }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Payment breakdown */}
        {eventOrders.length > 0 && (
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', marginBottom: '14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '10px' }}>{t('events.payment_breakdown')}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {Object.entries(
                eventOrders.filter(o => o.status !== 'return').reduce((acc, o) => {
                  const method = (o.payment_method || 'Unknown').split(' · ')[0]
                  acc[method] = (acc[method] || 0) + Number(o.total_amount || 0)
                  return acc
                }, {})
              ).sort((a, b) => b[1] - a[1]).map(([method, total]) => (
                <div key={method} style={{ padding: '8px 14px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)' }}>{method}</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--primary)', fontWeight: 600 }}>{fmt(total)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '4px', padding: '4px', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', background: 'var(--bg-card)', marginBottom: '16px', width: 'fit-content', overflowX: 'auto', boxShadow: 'var(--shadow-sm)' }}>
          {[
            { key: 'stock',     label: t('events.tab_stock'),     ic: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
            { key: 'sell',      label: t('events.tab_sell'),      ic: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-10 2a2 2 0 100 4 2 2 0 000-4z' },
            { key: 'orders',    label: t('events.tab_orders'),    ic: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            { key: 'analytics', label: t('events.tab_analytics'), ic: 'M18 20V10M12 20V4M6 20v-6' },
            { key: 'costs',     label: t('events.tab_costs'),     ic: 'M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: 'var(--radius-md)', border: 'none', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', background: activeTab === tab.key ? 'var(--primary)' : 'transparent', color: activeTab === tab.key ? '#fff' : 'var(--text-muted)', transition: 'all 0.12s', whiteSpace: 'nowrap', flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={tab.ic}/></svg>
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'stock' && (
          <div>
            <button onClick={() => { setShowAddStock(!showAddStock); setProductVariants([]) }}
              style={{ width: '100%', padding: '12px', borderRadius: 'var(--radius-md)', background: showAddStock ? 'var(--bg-card)' : 'var(--primary)', border: showAddStock ? '1px solid var(--border)' : 'none', fontSize: '13px', fontWeight: 600, color: showAddStock ? 'var(--text-muted)' : 'var(--bg-card)', cursor: 'pointer', marginBottom: '12px' }}>
              {showAddStock ? t('events.stock_move_cancel') : '+ ' + t('events.stock_move_btn')}
            </button>

            {showAddStock && (
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '16px', marginBottom: '12px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }} className="ev-grid">
                  <div style={{ gridColumn: 'span 2' }}>
                    <Field label={t('events.stock_form_product') + ' *'}>
                      <Sel value={newStock.product_id} onChange={v => { setNewStock(s => ({ ...s, product_id: v, variant_id: '' })); fetchVariantsForProduct(v) }}>
                        <option value="">Select product</option>
                        {products.map(p => <option key={p.id} value={p.id}>{p.name}{p.color ? ' — ' + p.color : ''} · stock: {p.stock}</option>)}
                      </Sel>
                    </Field>
                  </div>
                  {productVariants.length > 0 && (
                    <div style={{ gridColumn: 'span 2' }}>
                      <Field label={t('events.stock_form_variant') + ' *'}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {productVariants.map(v => (
                            <button key={v.id} onClick={() => setNewStock(s => ({ ...s, variant_id: v.id }))}
                              style={{ padding: '7px 12px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: newStock.variant_id === v.id ? 'var(--primary)' : 'var(--border)', background: newStock.variant_id === v.id ? 'var(--primary-light)' : 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: newStock.variant_id === v.id ? 'var(--primary)' : 'var(--text-primary)', cursor: 'pointer' }}>
                              {v.variant_name}<span style={{ fontSize: '10.5px', color: v.stock <= 3 ? RED : GREEN, marginLeft: '5px' }}>({v.stock})</span>
                            </button>
                          ))}
                        </div>
                      </Field>
                    </div>
                  )}
                  <Field label={t('events.stock_form_qty') + ' *'}><Inp type="number" value={newStock.quantity_sent} onChange={v => setNewStock(s => ({ ...s, quantity_sent: v }))} placeholder="0" min="1" /></Field>
                  <Field label={t('events.stock_form_notes')} optional><Inp value={newStock.notes} onChange={v => setNewStock(s => ({ ...s, notes: v }))} placeholder="Optional" /></Field>
                </div>
                <button onClick={handleAddStock}
                  disabled={savingStock || !newStock.product_id || !newStock.quantity_sent || (productVariants.length > 0 && !newStock.variant_id)}
                  style={{ width: '100%', padding: '11px', borderRadius: 'var(--radius-md)', background: 'var(--primary)', border: 'none', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer', opacity: (savingStock || !newStock.product_id || !newStock.quantity_sent) ? 0.6 : 1 }}>
                  {savingStock ? t('events.stock_moving') : t('events.stock_move_btn')}
                </button>
              </div>
            )}

            {eventStock.length === 0 ? (
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--border)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto 12px", display: "block" }}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{t('events.stock_no_stock')}</div>
                <div style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{t('events.stock_no_stock_desc')}</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {eventStock.map(s => {
                  const remaining = s.quantity_sent - s.quantity_sold - s.quantity_returned
                  const fullyDone = remaining <= 0
                  const soldPct   = s.quantity_sent > 0 ? Math.round((s.quantity_sold / s.quantity_sent) * 100) : 0
                  return (
                    <div key={s.id} className='pc-card-hover' style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', opacity: fullyDone ? 0.75 : 1, boxShadow: 'var(--shadow-sm)' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: 'var(--radius-md)', flexShrink: 0, overflow: 'hidden', background: 'var(--bg-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {s.products?.image_url ? <img src={s.products.image_url} alt={s.products?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon path="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" color={'var(--text-muted)'} size={18} strokeWidth={1.5} />}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{s.products?.name}</span>
                              {s.variant_name && <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: 'var(--primary-light)', color: 'var(--primary)' }}>{s.variant_name}</span>}
                              {s.products?.color && <span style={{ fontSize: '10.5px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--radius-full)', background: '#F5F3FF', color: PURPLE }}>{s.products.color}</span>}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace', fontWeight: 600 }}>{s.products?.product_code}</div>
                          </div>
                        </div>
                        <button onClick={() => handleDeleteStockRow(s)}
                          style={{ width: '26px', height: '26px', borderRadius: 'var(--radius-sm)', background: '#FEF2F2', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon path={IC.x} color={RED} size={11} strokeWidth={2.5} />
                        </button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '10px' }}>
                        {[
                          { label: t('events.stock_sent'),      value: s.quantity_sent,      color: 'var(--primary)' },
                          { label: t('events.stock_sold'),      value: s.quantity_sold,      color: GREEN },
                          { label: t('events.stock_returned'),  value: s.quantity_returned,  color: 'var(--text-muted)' },
                          { label: t('events.stock_remaining'), value: remaining,             color: remaining > 0 ? AMBER : 'var(--text-muted)' },
                        ].map(stat => (
                          <div key={stat.label} style={{ textAlign: 'center', padding: '10px 8px', borderRadius: 'var(--radius-md)', background: 'var(--bg-muted)', border: '1px solid var(--border)' }}>
                            <div style={{ fontSize: '18px', fontWeight: 700, color: stat.color }}>{stat.value}</div>
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{stat.label}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ height: '6px', borderRadius: 'var(--radius-sm)', background: 'var(--border)', marginBottom: '10px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: soldPct + '%', background: soldPct === 100 ? GREEN : 'var(--primary)', borderRadius: 'var(--radius-sm)', transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', textAlign: 'right' }}>{soldPct}% {t('events.stock_sold_pct')}</div>
                      {!fullyDone ? (
                        <button onClick={() => handleReturnStock(s)}
                          style={{ width: '100%', padding: '9px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '12.5px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}>
                          {t('events.stock_return_btn')} {remaining} {remaining !== 1 ? t('events.stock_units_pl') : t('events.stock_units')} {t('events.stock_return_to')}
                        </button>
                      ) : (
                        <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 600, color: GREEN, padding: '8px', background: '#F0FDF4', borderRadius: 'var(--radius-md)' }}>
                          {t('events.stock_accounted')}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            <button onClick={() => setShowDeleteConfirm(true)}
              style={{ width: '100%', padding: '10px', borderRadius: 'var(--radius-md)', background: '#FEF2F2', border: '1px solid #FECACA', fontSize: '12.5px', fontWeight: 600, color: RED, cursor: 'pointer', marginTop: '14px' }}>
              {t('events.stock_delete_event')}
            </button>
          </div>
        )}

        {/* ── QUICK SELL TAB ── */}
        {activeTab === 'quicksell' && (
          <div>
            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>{t('events.qs_sale_type')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {[
                  { key: 'instant', icon: '⚡', label: t('events.qs_instant'), desc: t('events.qs_instant_desc') },
                  { key: 'courier', icon: '🚚', label: t('events.qs_courier'), desc: t('events.qs_courier_desc') },
                  { key: 'lead',    icon: '📋', label: t('events.qs_lead'),    desc: t('events.qs_lead_desc') },
                ].map(m => (
                  <button key={m.key} onClick={() => setSellMode(m.key)}
                    style={{ padding: '12px', borderRadius: 'var(--radius-md)', border: '1px solid', borderColor: sellMode === m.key ? 'var(--primary)' : 'var(--border)', background: sellMode === m.key ? 'var(--primary-light)' : 'var(--bg-card)', textAlign: 'left', cursor: 'pointer' }}>
                    <div style={{ fontSize: '18px', marginBottom: '4px' }}>{m.icon}</div>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: sellMode === m.key ? 'var(--primary)' : 'var(--text-primary)' }}>{m.label}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{m.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon path={IC.cart} color={'var(--primary)'} size={14} strokeWidth={2} />
                  <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('events.qs_products')}</span>
                </div>
                <button onClick={() => setCart(c => [...c, emptyCartLine()])}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: 'var(--radius-md)', background: 'var(--primary-light)', border: 'none', fontSize: '12px', fontWeight: 600, color: 'var(--primary)', cursor: 'pointer' }}>
                  <Icon path={IC.plus} color={'var(--primary)'} size={11} strokeWidth={2.5} /> {t('events.qs_add_product')}
                </button>
              </div>
              {cart.map((item, index) => (
                <EventCartRow key={index} item={item} index={index}
                  onUpdate={(i, updates) => setCart(c => c.map((l, idx) => idx === i ? { ...l, ...updates } : l))}
                  onRemove={(i) => setCart(c => c.filter((_, idx) => idx !== i))}
                  sym={sym} eventStock={eventStock} products={products} t={t} />
              ))}
            </div>

            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>{t('events.qs_payment')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {QS_PAYMENTS.map(m => (
                  <button key={m} onClick={() => setPaymentMethod(m)}
                    style={{ padding: '7px 14px', borderRadius: 'var(--radius-full)', border: '1px solid', borderColor: paymentMethod === m ? 'var(--primary)' : 'var(--border)', background: paymentMethod === m ? 'var(--primary-light)' : 'var(--bg-card)', fontSize: '12px', fontWeight: 600, color: paymentMethod === m ? 'var(--primary)' : 'var(--text-muted)', cursor: 'pointer' }}>
                    {m}
                  </button>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }} className="ev-grid">
                <Field label={t('events.qs_discount')} optional><Inp type="number" value={orderDiscount} onChange={setOrderDiscount} placeholder="0" /></Field>
                <Field label={t('events.qs_vat')} optional><Inp type="number" value={orderVat} onChange={setOrderVat} placeholder="0" /></Field>
              </div>
            </div>

            <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '14px', marginBottom: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                {t('events.qs_customer')} {sellMode === 'instant' ? t('events.qs_customer_instant') : t('events.qs_customer_required')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }} className="ev-grid">
                <Field label={t('events.qs_name')} optional><Inp value={customerName} onChange={setCustomerName} placeholder="Customer name" /></Field>
                <Field label={t('events.qs_phone')} optional><Inp value={customerPhone} onChange={setCustomerPhone} placeholder="Phone number" /></Field>
                <Field label={t('events.qs_sold_by')} optional><Inp value={takenBy} onChange={setTakenBy} placeholder={t('events.qs_staff_ph')} /></Field>
                <Field label={t('events.qs_note')} optional><Inp value={saleNote} onChange={setSaleNote} placeholder={t('events.qs_note_ph')} /></Field>
              </div>
            </div>

            {cart.some(l => l.product_id) && (
              <div style={{ background: 'var(--text-primary)', borderRadius: 'var(--radius-lg)', padding: '14px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>{t('events.qs_summary')}</div>
                {cart.filter(l => l.product_id).map((l, i) => {
                  const lc = calcLine(l)
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.8)', marginBottom: '6px' }}>
                      <span>{l.product_name}{l.product_color ? ' (' + l.product_color + ')' : ''}{l.variant_name ? ' · ' + l.variant_name : ''} ×{l.quantity}</span>
                      <span style={{ fontWeight: 600, color: '#fff' }}>{sym}{lc.subtotal.toLocaleString()}</span>
                    </div>
                  )
                })}
                {(orderDiscAmt > 0 || orderVatAmt > 0) && (
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', marginTop: '8px' }}>
                    {orderDiscAmt > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}><span>{t('events.qs_discount_row')} ({orderDiscount}%)</span><span style={{ color: '#FCA5A5' }}>-{sym}{orderDiscAmt.toLocaleString()}</span></div>}
                    {orderVatAmt > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}><span>{t('events.qs_vat_row')} ({orderVat}%)</span><span>+{sym}{orderVatAmt.toLocaleString()}</span></div>}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '10px', marginTop: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>{t('events.qs_total')}</span>
                  <span style={{ fontSize: '22px', fontWeight: 700, color: 'var(--primary)' }}>{sym}{grandTotal.toLocaleString()}</span>
                </div>
              </div>
            )}

            <button onClick={handleQuickSell} disabled={selling || !cart.some(l => l.product_id)}
              style={{ width: '100%', padding: '14px', borderRadius: 'var(--radius-lg)', background: selling || !cart.some(l => l.product_id) ? 'var(--border)' : sellMode === 'instant' ? 'var(--primary)' : sellMode === 'courier' ? '#1D4ED8' : 'var(--text-primary)', border: 'none', fontSize: '14px', fontWeight: 700, color: '#fff', cursor: (selling || !cart.some(l => l.product_id)) ? 'not-allowed' : 'pointer' }}>
              {selling ? t('events.qs_selling') :
               sellMode === 'instant' ? t('events.qs_sell_instant') + ' — ' + sym + grandTotal.toLocaleString() :
               sellMode === 'courier' ? t('events.qs_sell_courier') + ' — ' + sym + grandTotal.toLocaleString() :
               t('events.qs_sell_lead') + ' — ' + sym + grandTotal.toLocaleString()}
            </button>
          </div>
        )}

        {/* ── ORDERS TAB ── */}
        {activeTab === 'orders' && (
          <div>
            {eventOrders.length === 0 ? (
              <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', padding: '48px', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '10px' }}>🧾</div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{t('events.orders_no_orders')}</div>
                <div style={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>{t('events.orders_no_orders_desc')}</div>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                  {[
                    { label: t('events.orders_completed'), value: eventOrders.filter(o => o.status === 'closed').length,                                                           color: GREEN, bg: '#F0FDF4' },
                    { label: t('events.orders_pending'),   value: eventOrders.filter(o => ['received','pending','confirmed','processing'].includes(o.status)).length,              color: AMBER, bg: '#FFF8E6' },
                    { label: t('events.orders_returns'),   value: eventOrders.filter(o => o.status === 'return').length,                                                            color: RED,   bg: '#FEF2F2' },
                  ].map((s, i) => (
                    <div key={i} style={{ textAlign: 'center', padding: '10px', borderRadius: 'var(--radius-md)', background: s.bg, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 130px 1fr 120px 100px 90px 60px', gap: '8px', padding: '10px 16px', background: 'var(--bg-muted)', borderBottom: '1px solid var(--border)' }} className="ev-row-desktop">
                    {[t('events.orders_col_sl'), t('events.orders_col_order'), t('events.orders_col_customer'), t('events.orders_col_product'), t('events.orders_col_amount'), t('events.orders_col_status'), ''].map((h, i) => (
                      <div key={i} style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</div>
                    ))}
                  </div>
                  {eventOrders.map((order, idx) => (
                    <div key={order.id}>
                      <div style={{ display: 'grid', gridTemplateColumns: '36px 130px 1fr 120px 100px 90px 60px', gap: '8px', padding: '11px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center', cursor: 'pointer', transition: 'background 0.1s' }}
                        className="ev-row-desktop"
                        onClick={() => navigate('/orders/' + order.id)}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-card)'}>
                        <div style={{ fontSize: '11.5px', fontWeight: 600, color: 'var(--text-muted)' }}>{idx + 1}</div>
                        <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>{order.order_code}</div>
                        <div>
                          <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{order.customers?.name || t('events.orders_walkin')}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{order.customers?.phone || new Date(order.created_at).toLocaleString('en-BD', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.product_name || '—'}</div>
                        <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(order.total_amount)}</div>
                        <div><StatusPill status={order.status} /></div>
                        <button onClick={e => { e.stopPropagation(); navigate('/orders/' + order.id) }}
                          style={{ width: '26px', height: '26px', borderRadius: 'var(--radius-sm)', border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(11,31,58,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <Icon path={IC.eye} color={'var(--primary)'} size={13} strokeWidth={2} />
                        </button>
                      </div>
                      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                        className="ev-row-mobile"
                        onClick={() => navigate('/orders/' + order.id)}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace' }}>{order.order_code}</span>
                          <StatusPill status={order.status} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{order.customers?.name || t('events.orders_walkin')}</div>
                            <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{order.product_name}</div>
                          </div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{fmt(order.total_amount)}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Delete confirm */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)', padding: '28px', maxWidth: '360px', width: '100%', fontFamily: 'var(--font-ui)', textAlign: 'center' }}>
            <div style={{ fontSize: '36px', marginBottom: '10px' }}>🗑️</div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '6px' }}>{t('events.delete_title')}</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', marginBottom: '6px', lineHeight: 1.6 }}>{t('events.delete_desc')}</div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setShowDeleteConfirm(false)}
                style={{ flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
                {t('events.delete_cancel')}
              </button>
              <button onClick={handleDeleteEvent}
                style={{ flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', background: RED, border: 'none', fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer' }}>
                {t('events.delete_confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt */}
      {receiptData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '480px', maxHeight: '80vh', overflow: 'auto', fontFamily: 'var(--font-ui)' }}>
            <div style={{ padding: '14px 18px', background: 'var(--text-primary)', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ color: '#fff', fontSize: '14px', fontWeight: 700 }}>{t('events.receipt_title')}</span>
              <button onClick={() => setReceiptData(null)}
                style={{ padding: '6px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
                {t('events.receipt_done')}
              </button>
            </div>
            <div style={{ padding: '18px' }}>
              <div style={{ textAlign: 'center', padding: '16px', borderRadius: 'var(--radius-lg)', border: '2px dashed var(--border)', marginBottom: '16px' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>✅</div>
                <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '4px' }}>{sym}{receiptData.total.toLocaleString()}</div>
                <div style={{ fontSize: '12px', color: 'var(--primary)', fontFamily: 'JetBrains Mono, monospace', marginBottom: '4px' }}>{receiptData.order?.order_code}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--text-muted)' }}>{paymentMethod} · {new Date().toLocaleString('en-BD', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>{t('events.receipt_items')}</div>
              {receiptData.items.filter(l => l.product_id).map((l, i) => {
                const lc = calcLine(l)
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ color: 'var(--text-primary)' }}>{l.product_name}{l.product_color ? ' (' + l.product_color + ')' : ''}{l.variant_name ? ' · ' + l.variant_name : ''} ×{l.quantity}</span>
                    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{sym}{lc.subtotal.toLocaleString()}</span>
                  </div>
                )
              })}
              <button onClick={() => window.print()}
                style={{ width: '100%', marginTop: '14px', padding: '11px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'var(--bg-card)', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' }}>
                {t('events.receipt_print')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes ev-spin { to { transform: rotate(360deg) } }
        .ev-row-desktop { display: grid !important; }
        .ev-row-mobile  { display: none !important; }
        @media(max-width:800px) {
          .ev-stat-grid   { grid-template-columns: 1fr 1fr !important; }
          .ev-row-desktop { display: none !important; }
          .ev-row-mobile  { display: block !important; }
          .ev-grid        { grid-template-columns: 1fr !important; }
          .ev-chart-grid  { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  )
}

export default Events